import type { AngUnit, Units } from './types';

const ANG: readonly AngUnit[] = ['radians', 'grads', 'decimal degrees', 'decimal dd.mm.ss'];

export function asAngUnit(v: string | undefined, fallback: AngUnit = 'radians'): AngUnit {
  if (!v) return fallback;
  const t = v.trim().toLowerCase();
  for (const a of ANG) if (a.toLowerCase() === t) return a;
  // Tolerate the spellings writers actually emit.
  if (t === 'degrees' || t === 'degree' || t === 'decimaldegrees') return 'decimal degrees';
  if (t === 'gradians' || t === 'gon' || t === 'gons') return 'grads';
  if (t === 'dd.mm.ss' || t === 'decimal dms' || t === 'dms') return 'decimal dd.mm.ss';
  if (t === 'radian') return 'radians';
  return fallback;
}

/**
 * Convert an angular value to radians.
 *
 * `decimal dd.mm.ss` is packed decimal: 45.3025 means 45deg 30' 25". Parse from the
 * raw attribute string where possible - the packed form loses precision through a
 * double round-trip on long values.
 */
export function angleToRad(v: number, u: AngUnit): number {
  switch (u) {
    case 'radians': return v;
    case 'grads': return (v * Math.PI) / 200;
    case 'decimal degrees': return (v * Math.PI) / 180;
    case 'decimal dd.mm.ss': {
      const s = v < 0 ? -1 : 1;
      const a = Math.abs(v);
      const d = Math.trunc(a);
      const r = (a - d) * 100;
      const m = Math.trunc(r + 1e-9);
      const sec = (r - m) * 100;
      return (s * (d + m / 60 + sec / 3600) * Math.PI) / 180;
    }
  }
}

const LINEAR_TO_M: Record<string, number> = {
  millimeter: 0.001,
  millimetre: 0.001,
  centimeter: 0.01,
  centimetre: 0.01,
  meter: 1,
  metre: 1,
  kilometer: 1000,
  kilometre: 1000,
  foot: 0.3048,
  feet: 0.3048,
  ft: 0.3048,
  ussurveyfoot: 1200 / 3937,
  'us survey foot': 1200 / 3937,
  inch: 0.0254,
  mile: 1609.344,
};

export function linearToMeters(unit: string | undefined): number {
  if (!unit) return 1;
  return LINEAR_TO_M[unit.trim().toLowerCase()] ?? 1;
}

/** Human label for the status bar, e.g. `m`, `ft`, `USft`. */
export function linearAbbrev(unit: string | undefined): string {
  switch ((unit ?? '').toLowerCase()) {
    case 'millimeter': case 'millimetre': return 'mm';
    case 'centimeter': case 'centimetre': return 'cm';
    case 'meter': case 'metre': return 'm';
    case 'kilometer': case 'kilometre': return 'km';
    case 'foot': return 'ft';
    case 'ussurveyfoot': return 'USft';
    case 'inch': return 'in';
    case 'mile': return 'mi';
    default: return unit || '?';
  }
}

export function defaultUnits(): Units {
  return {
    system: 'metric',
    linear: 'meter',
    angular: 'radians',
    direction: 'radians',
    linearToMeters: 1,
    raw: {},
  };
}
